import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "../../utils/axios";
import config from "../../config";
import {
  Box, Typography, Snackbar, Alert, TextField, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, Tooltip, IconButton, Switch, Paper, Select, MenuItem
} from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export default function Links() {
  const [links, setLinks] = useState([]);
  const [snack, setSnack] = useState({ open: false, text: '', severity: 'success' });
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [total, setTotal] = useState(0);
  const [selectedRowIds, setSelectedRowIds] = useState({});
  const token = localStorage.getItem("token");

  // Fetch links với phân trang + tìm kiếm
  const fetchLinks = useCallback(async () => {
    setLoading(true);
    try {
      const params = [
        `page=${page + 1}`,
        `pageSize=${pageSize}`,
        search ? `q=${encodeURIComponent(search)}` : ""
      ].filter(Boolean).join("&");

      const res = await axios.get(
        `/teolucpro/links?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLinks(res.data.links.map(link => ({
        ...link,
        user: link.users?.username || "",
        clicks: link.totalClicks || 0,
        _id: link.id,
      })));
      setTotal(res.data.total);
    } catch (err) {
      setSnack({ open: true, text: "Lỗi tải dữ liệu link", severity: "error" });
    } finally {
      setLoading(false);
    }
  }, [token, page, pageSize, search]);

  useEffect(() => { fetchLinks(); }, [fetchLinks]);

  // Xoá link
  const handleDelete = useCallback(async (row) => {
    if (!window.confirm(`Xác nhận xoá link ${row.short_code}?`)) return;
    try {
      await axios.delete(`/teolucpro/links/${row.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSnack({ open: true, text: "Đã xoá link", severity: "success" });
      fetchLinks();
    } catch {
      setSnack({ open: true, text: "Lỗi xoá link", severity: "error" });
    }
  }, [token, fetchLinks]);

  // Duyệt/khoá link
  const handleApprove = useCallback(async (row) => {
    try {
      await axios.patch(`/teolucpro/links/${row.id}`, {
        is_active: !row.is_active,
        original_url: row.original_url,
        note: row.note || "",
        link_name: row.link_name || ""
      }, { headers: { Authorization: `Bearer ${token}` } });
      setSnack({ open: true, text: "Cập nhật trạng thái thành công!", severity: "success" });
      fetchLinks();
    } catch {
      setSnack({ open: true, text: "Lỗi cập nhật trạng thái!", severity: "error" });
    }
  }, [token, fetchLinks]);

  // Lưu chỉnh sửa
  const handleSaveEdit = async () => {
    try {
      await axios.patch(`/teolucpro/links/${editing.id}`, {
        original_url: editing.original_url,
        note: editing.note || "",
        is_active: editing.is_active,
        link_name: editing.link_name || ""
      }, { headers: { Authorization: `Bearer ${token}` } });
      setSnack({ open: true, text: "Đã cập nhật!", severity: "success" });
      setEditing(null);
      fetchLinks();
    } catch (err) {
      setSnack({ open: true, text: "Lỗi cập nhật!", severity: "error" });
    }
  };

  // Columns for TanStack Table
  const columns = useMemo(() => [
    {
      id: "select",
      header: ({ table }) => (
        <input
          type="checkbox"
          checked={table.getIsAllRowsSelected()}
          onChange={table.getToggleAllRowsSelectedHandler()}
        />
      ),
      cell: ({ row }) => (
        <input
          type="checkbox"
          checked={row.getIsSelected()}
          onChange={row.getToggleSelectedHandler()}
        />
      ),
      size: 32,
      enableSorting: false,
      enableResizing: false,
    },
    {
      accessorKey: "id",
      header: "ID",
      cell: info => <b>{info.getValue()}</b>,
      size: 60,
    },
    {
      accessorKey: "short_code",
      header: "Short",
      cell: info => (
        <Tooltip title={config.BASE_URL + "/" + info.getValue()}>
          <a
            href={`${config.BASE_URL}/${info.getValue()}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "#1976d2",
              fontWeight: 700,
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              textDecoration: "none",
              borderRadius: 6,
              padding: "1px 5px",
              transition: "background 0.2s",
            }}
            onMouseEnter={e => (e.currentTarget.style.background = "#e3f2fd")}
            onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
          >
            <OpenInNewIcon fontSize="small" sx={{ mr: 0.2, color: "#90caf9" }} />
            <u>{info.getValue()}</u>
          </a>
        </Tooltip>
      ),
      size: 110,
    },
    {
      accessorKey: "original_url",
      header: "URL gốc",
      cell: info => (
        <Tooltip title={info.getValue()}>
          <span style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: 220,
            display: "block",
            color: "#1565c0"
          }}>{info.getValue()}</span>
        </Tooltip>
      ),
      size: 260,
    },
    {
      accessorKey: "link_name",
      header: "Tên link",
      cell: info => (
        <Tooltip title={info.getValue()}>
          <span style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            width: "100%",
            display: "block",
            fontWeight: 500,
            color: "#374151"
          }}>{info.getValue()}</span>
        </Tooltip>
      ),
    },
    {
      accessorKey: "user",
      header: "Người tạo",
      size: 110,
      cell: info => <span>{info.getValue()}</span>
    },
    {
      accessorKey: "clicks",
      header: "Lượt click",
      size: 90,
      cell: info => <span>{info.getValue()}</span>
    },
    {
      accessorKey: "created_at",
      header: "Ngày tạo",
      size: 140,
      cell: info => <span style={{ fontSize: 13 }}>{info.getValue()}</span>
    },
    {
      accessorKey: "is_active",
      header: "Trạng thái",
      size: 110,
      cell: info => info.getValue()
        ? <span style={{ color: "#2e7d32", fontWeight: 700, borderRadius: 16, padding: "3px 11px", background: "#e8f5e9" }}>Hoạt động</span>
        : <span style={{ color: "#c62828", fontWeight: 700, borderRadius: 16, padding: "3px 11px", background: "#ffebee" }}>Tạm khoá</span>
    },
    {
      id: "actions",
      header: "Thao tác",
      cell: ({ row }) => (
        <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
          <Tooltip title="Duyệt/Kích hoạt">
            <IconButton size="small" sx={{
              color: "#2e7d32", bgcolor: "#e8f5e9", borderRadius: 2,
              "&:hover": { bgcolor: "#c8e6c9", color: "#1565c0", boxShadow: 1 }
            }} onClick={() => handleApprove(row.original)}>
              <CheckCircleIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Sửa">
            <IconButton size="small" sx={{
              color: "#1565c0", bgcolor: "#e3f2fd", borderRadius: 2,
              "&:hover": { bgcolor: "#bbdefb", color: "#2e7d32", boxShadow: 1 }
            }} onClick={() => setEditing({ ...row.original })}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Xoá">
            <IconButton size="small" sx={{
              color: "#c62828", bgcolor: "#ffebee", borderRadius: 2,
              "&:hover": { bgcolor: "#ffcdd2", color: "#1565c0", boxShadow: 1 }
            }} onClick={() => handleDelete(row.original)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
      size: 140,
      enableSorting: false,
    }
  ], [handleApprove, handleDelete]);

  // Table instance
  const table = useReactTable({
    data: links,
    columns,
    state: {
      pagination: {
        pageIndex: page,
        pageSize,
      },
      rowSelection: selectedRowIds,
    },
    manualPagination: true,
    pageCount: Math.ceil(total / pageSize),
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableRowSelection: true,
    onPaginationChange: updater => {
      if (typeof updater === "function") {
        const next = updater({ pageIndex: page, pageSize });
        setPage(next.pageIndex ?? 0);
        setPageSize(next.pageSize ?? 25);
      } else {
        setPage(updater.pageIndex ?? 0);
        setPageSize(updater.pageSize ?? 25);
      }
    },
    onRowSelectionChange: setSelectedRowIds,
  });

  // Pagination button MUI style
  function MuiPaginateBtn({ onClick, disabled, children, icon }) {
    return (
      <IconButton
        onClick={onClick}
        disabled={disabled}
        size="small"
        sx={{
          border: "1px solid #1976d2",
          color: "#1976d2",
          mx: 0.5,
          bgcolor: "#fff",
          "&:hover": { bgcolor: "#1976d2", color: "#fff" },
          borderRadius: 1.5,
          ...(disabled && {
            opacity: 0.6, bgcolor: "#f5f5f5", borderColor: "#ddd", color: "#bdbdbd"
          })
        }}
      >
        {icon || children}
      </IconButton>
    );
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight={800} sx={{ mb: 3 }}>
        Quản lý link rút gọn
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 2 }}>
        <TextField
          placeholder="Tìm kiếm Short code hoặc URL..."
          size="small"
          value={search}
          onChange={e => {
            setSearch(e.target.value);
            setPage(0);
          }}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: "#1976d2" }} />
          }}
          sx={{
            maxWidth: 360, bgcolor: "#fff", borderRadius: 2,
            boxShadow: "0 2px 12px #6366f122"
          }}
        />
      </Box>
      <Paper
        elevation={2}
        sx={{
          bgcolor: "#fff",
          borderRadius: 3,
          p: 2,
          boxShadow: "0 8px 40px #6366f119",
          overflow: "visible"
        }}
      >
        <Box sx={{ width: "100%", overflowX: "auto", overflowY: "hidden" }}>
          <Box sx={{
            maxHeight: 500,
            minHeight: 250,
            overflowY: "auto",
            borderRadius: 2,
            '&::-webkit-scrollbar-thumb': { bgcolor: "#b2dfdb" },
            '&::-webkit-scrollbar': { height: 8, borderRadius: 8 }
          }}>
            <table
              style={{
                borderCollapse: "separate",
                borderSpacing: 0,
                minWidth: 1100,
                background: "#fff",
                borderRadius: 16,
                boxShadow: "0 1px 6px #e3f2fd"
              }}
            >
              <thead>
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <th
                        key={header.id}
                        style={{
                          fontWeight: 700,
                          background: "#f5f8fa",
                          color: "#1976d2",
                          padding: "10px 8px",
                          borderBottom: "1.5px solid #e0e0e0",
                          fontSize: 15,
                          textAlign: "center",
                          position: "sticky",
                          top: 0,
                          zIndex: 2
                        }}
                        colSpan={header.colSpan}
                      >
                        {header.isPlaceholder ? null : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={columns.length} style={{ textAlign: "center", padding: 44 }}>
                      Đang tải dữ liệu...
                    </td>
                  </tr>
                ) : table.getRowModel().rows.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} style={{ textAlign: "center", padding: 44, color: "#888" }}>
                      Không có dữ liệu.
                    </td>
                  </tr>
                ) : (
                  table.getRowModel().rows.map((row, idx) => (
                    <tr key={row.id}
                      style={{
                        borderRadius: 14,
                        background: row.getIsSelected()
                          ? "#e3f2fd"
                          : idx % 2 === 0 ? "#fafbfc" : "#fff",
                        transition: "background .18s"
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = "#f0f7ff"}
                      onMouseLeave={e => e.currentTarget.style.background = row.getIsSelected()
                        ? "#e3f2fd"
                        : idx % 2 === 0
                          ? "#fafbfc"
                          : "#fff"}
                    >
                      {row.getVisibleCells().map(cell => (
                        <td key={cell.id}
                          style={{
                            padding: "8px 8px",
                            fontSize: 15,
                            textAlign: "center",
                            borderBottom: "1px solid #f0f0f0",
                            verticalAlign: "middle",
                            background: "inherit"
                          }}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </Box>
        </Box>
        {/* Pagination */}
        <Box sx={{
          mt: 2, display: "flex", alignItems: "center", justifyContent: "space-between"
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <span style={{ fontSize: 15 }}>Rows per page:</span>
            <Select
              value={pageSize}
              size="small"
              onChange={e => { setPageSize(Number(e.target.value)); setPage(0); }}
              sx={{
                minWidth: 66,
                borderRadius: 2,
                fontSize: 15,
                boxShadow: "0 1px 5px #e0e7ef2a"
              }}
            >
              {PAGE_SIZE_OPTIONS.map(sz =>
                <MenuItem value={sz} key={sz}>{sz}</MenuItem>
              )}
            </Select>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <MuiPaginateBtn
              onClick={() => setPage(0)}
              disabled={page === 0}
              icon={<ArrowBackIosNewIcon sx={{ fontSize: 15, mr: "-2px" }} />}
            />
            <MuiPaginateBtn
              onClick={() => setPage(page - 1)}
              disabled={page === 0}
              icon={<ArrowBackIosNewIcon sx={{ fontSize: 16 }} />}
            />
            <span style={{ fontWeight: 600, margin: "0 8px", fontSize: 15, color: "#374151" }}>
              {page + 1} / {Math.max(1, table.getPageCount())}
            </span>
            <MuiPaginateBtn
              onClick={() => setPage(page + 1)}
              disabled={page >= table.getPageCount() - 1}
              icon={<ArrowForwardIosIcon sx={{ fontSize: 16 }} />}
            />
            <MuiPaginateBtn
              onClick={() => setPage(table.getPageCount() - 1)}
              disabled={page >= table.getPageCount() - 1}
              icon={<ArrowForwardIosIcon sx={{ fontSize: 15, ml: "-2px" }} />}
            />
          </Box>
        </Box>
      </Paper>
      {/* Dialog sửa */}
      <Dialog open={!!editing} onClose={() => setEditing(null)}>
        <DialogTitle>Cập nhật link rút gọn</DialogTitle>
        {editing && (
          <DialogContent>
            <TextField
              margin="normal"
              label="Short code"
              fullWidth
              disabled
              value={editing.short_code}
            />
            <TextField
              margin="normal"
              label="URL gốc"
              fullWidth
              value={editing.original_url}
              onChange={e => setEditing({ ...editing, original_url: e.target.value })}
            />
            <TextField
              margin="normal"
              label="Tên link"
              fullWidth
              value={editing.link_name || ""}
              onChange={e => setEditing({ ...editing, link_name: e.target.value })}
            />
            <TextField
              margin="normal"
              label="Ghi chú"
              fullWidth
              value={editing.note || ""}
              onChange={e => setEditing({ ...editing, note: e.target.value })}
            />
            <Box sx={{ mt: 2 }}>
              <span style={{ marginRight: 12, fontWeight: 500 }}>
                {editing.is_active ? "Hoạt động" : "Tạm khoá"}
              </span>
              <Switch
                checked={editing.is_active}
                onChange={e => setEditing({ ...editing, is_active: e.target.checked })}
                color="success"
              />
            </Box>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Hủy</Button>
          <Button onClick={handleSaveEdit} variant="contained">Lưu</Button>
        </DialogActions>
      </Dialog>
      {/* Snackbar thông báo */}
      <Snackbar
        open={snack.open}
        autoHideDuration={2200}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        onClose={() => setSnack({ ...snack, open: false })}
      >
        <Alert severity={snack.severity} sx={{ width: '100%' }}>
          {snack.text}
        </Alert>
      </Snackbar>
    </Box>
  );
}
